/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { PROFESSIONAL_CATEGORIES } from '@/lib/utils-cat';
import Logo from '@/components/Logo';
import { toast } from 'sonner';
import { Mail, Lock, User, Briefcase, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import { AVAILABLE_LANGUAGES } from '@/i18n';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: '',
    category: 'medici',
    specialty: '',
    bio: '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const changeLang = (code) => { i18n.changeLanguage(code); localStorage.setItem('sc_lang', code); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.full_name || !form.category) return;
    setLoading(true);
    try {
      await register({ ...form, role: 'professional', email: form.email.trim(), full_name: form.full_name.trim() });
      toast.success(t('registerSuccess'));
      navigate('/feed');
    } catch (err) {
      const msg = err.response?.data?.detail || t('errorOccurred');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-grain flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }}
        className="w-full max-w-lg"
      >
        <div className="flex items-center justify-between mb-6">
          <Logo size={44} withText textClassName="text-base" tagline={t('tagline')} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="text-[11px] px-3 py-1.5 rounded-full uppercase font-semibold bg-white/70 hover:bg-white flex items-center gap-1"
                data-testid="auth-lang-trigger"
              >
                <Globe className="h-3 w-3" /> {i18n.language.toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[60vh] overflow-y-auto">
              {AVAILABLE_LANGUAGES.map(l => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => changeLang(l.code)}
                  data-testid={`auth-lang-${l.code}`}
                  className={i18n.language === l.code ? 'font-semibold text-primary' : ''}
                >
                  <span className="mr-2 text-base">{l.flag}</span>{l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Card className="p-6 rounded-2xl bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
          <h1 className="font-display font-semibold text-xl mb-1">{t('welcome')}</h1>
          <p className="text-sm text-muted-foreground mb-5">{t('register')}</p>

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <Label className="text-xs text-muted-foreground">{t('fullName')}</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={form.full_name} onChange={(e) => set('full_name', e.target.value)} className="pl-9" data-testid="register-fullname-input" required />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">{t('email')}</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="pl-9" data-testid="register-email-input" required />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t('password')}</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} className="pl-9" data-testid="register-password-input" minLength={6} required />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">{t('professionalCategory')}</Label>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="register-category-group">
                {PROFESSIONAL_CATEGORIES.map(([key, meta]) => {
                  const Icon = meta.icon;
                  const active = form.category === key;
                  return (
                    <button
                      key={key} type="button"
                      onClick={() => set('category', key)}
                      className={`p-3 rounded-xl border transition-all text-left ${active ? 'ring-2 ring-offset-1' : 'hover:bg-secondary/60'}`}
                      style={active ? { backgroundColor: meta.soft, borderColor: meta.ring, boxShadow: `0 0 0 1px ${meta.ring}` } : { borderColor: '#E2E8F0', backgroundColor: '#fff' }}
                      data-testid={`register-category-${key}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" style={{ color: meta.textOnSoft }} />
                        <div className="font-semibold text-sm" style={{ color: meta.textOnSoft }}>{t(meta.label)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">{t('specialty')}</Label>
              <div className="relative mt-1">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={form.specialty} onChange={(e) => set('specialty', e.target.value)} className="pl-9" data-testid="register-specialty-input" />
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">{t('bio')}</Label>
              <Textarea value={form.bio} onChange={(e) => set('bio', e.target.value)} rows={2} data-testid="register-bio-input" />
            </div>

            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
              {loading ? t('loading') : t('register')}
            </Button>
          </form>
          <div className="mt-5 text-sm text-center text-muted-foreground">
            {t('alreadyHaveAccount')} <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-goto-login">{t('loginNow')}</Link>
          </div>
        </Card>
        <div className="mt-6 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Globe className="h-3 w-3" /> {AVAILABLE_LANGUAGES.length} lingue disponibili
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
